/**
 * Modulversionen in der Shell (ADR-010, Nachtrag P10 „Laufzeittausch“).
 *
 * Seit P10 tauscht die API eine Modulversion ohne Neustart. Die Shell merkt das in zwei Stufen:
 * `GET /api/core/health` (öffentlich) liefert nur eine Kennung des geladenen Stands
 * (`module.modulStand`, ein Hash — keine Modulnamen oder Versionen, Sicherheitsreview F2). Ändert
 * sie sich, holt die Shell das angemeldete Bootstrap und vergleicht die Version des geöffneten
 * Moduls. Folgen:
 *
 * - Der Bundle-Zwischenspeicher des Loaders gilt je **Modul und Version** (`id@version`), sonst
 *   bliebe nach einem Tausch das alte Bundle im Speicher, obwohl das Manifest das neue nennt.
 * - Läuft im Backend eine andere Version als die geöffnete Oberfläche, zeigt die Shell den
 *   Hinweis „Neue Version – neu laden“, statt still zwei Versionen zu mischen.
 *
 * Reine Funktionen ohne DOM, damit sie testbar sind.
 */

/** Was die Shell von einem Manifest-Eintrag dafür braucht. */
export interface VersionsEintrag {
  id: string;
  version?: string;
  integrity: string;
}

/**
 * Schlüssel des Bundle-Zwischenspeichers: `id@version`. Ohne Version (Datei-Manifest, ADR-002)
 * zählt die Prüfsumme — sie ändert sich mit jedem neuen Bundle ebenso.
 */
export function cacheSchluessel(eintrag: VersionsEintrag): string {
  return `${eintrag.id}@${eintrag.version ?? eintrag.integrity}`;
}

/** Ob ein Schlüssel zu einem Modul gehört (zum Aufräumen älterer Versionen). */
export function gehoertZuModul(schluessel: string, id: string): boolean {
  return schluessel.startsWith(`${id}@`);
}

/** Kennung des geladenen Modulstands aus `/core/health` (oder `null`, z. B. ältere API). */
export function modulStandAus(health: unknown): string | null {
  const stand = (health as { module?: { modulStand?: unknown } } | null)?.module?.modulStand;
  return typeof stand === 'string' && stand ? stand : null;
}

/**
 * Die Version laut (neu geladenem) Manifest, wenn sie sich von der geöffneten Oberfläche
 * unterscheidet — sonst `null`. Ohne Versionsangabe (Datei-Manifest) nie ein Hinweis.
 */
export function neueVersion(eintrag: VersionsEintrag, module: unknown): string | null {
  if (!eintrag.version || !Array.isArray(module)) return null;
  const neu = (module as { id?: unknown; version?: unknown }[]).find((m) => m?.id === eintrag.id);
  if (!neu || typeof neu.version !== 'string' || !neu.version) return null;
  return neu.version !== eintrag.version ? neu.version : null;
}
